import { logError } from '@bonanza-social/toolkit/logging';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { uuid } from '@bonanza-social/toolkit/utils';

export const getDeviceId = async () => {
  const storedId = await AsyncStorage.getItem('DEVICE_ID');

  if (storedId) {
    return storedId;
  }

  const id = uuid();

  await AsyncStorage.setItem('DEVICE_ID', id);

  return id;
};

export const getSessionId = () => {
  const storedId = sessionStorage.getItem('SESSION_ID');

  if (storedId) {
    return storedId;
  }

  const id = uuid();

  sessionStorage.setItem('SESSION_ID', id);

  return id;
};

import SHA256 from 'sha.js';

export const getDeviceMetadata = () => {
  try {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    const hasLocalStorage = !!window.localStorage;
    const hasSessionStorage = !!window.sessionStorage;
    const colorDepth = window.screen.colorDepth;

    const toInt = (value: unknown) => parseInt(value as string);

    const toFloat = (value: unknown) => parseFloat(value as string);

    const replaceNaN = <T, U>(value: T, replacement: U): T | U =>
      typeof value === 'number' && isNaN(value) ? replacement : value;

    const getApplePay = () => {
      const { ApplePaySession } = window;

      if (typeof ApplePaySession?.canMakePayments !== 'function') {
        return 'No Api';
      }

      return ApplePaySession.canMakePayments() ? 'Enabled' : 'Disabled';
    };

    const getCanvasFingerprint = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Hello, world!', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Hello, world!', 4, 17);
      }
      return SHA256('sha256').update(canvas.toDataURL()).digest('hex');
    };

    const getWebGLFingerprint = () => {
      const canvas = document.createElement('canvas');
      const gl =
        canvas.getContext('webgl') ||
        (canvas.getContext(
          'experimental-webgl',
        ) as WebGLRenderingContext | null);

      if (!gl) return '';

      const ext = gl.getExtension('WEBGL_debug_renderer_info');

      if (!ext) return '';

      const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
      const vendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL);
      return { vendor, renderer };
    };

    const areCookiesEnabled = () => {
      const d = document;

      try {
        // Create cookie
        d.cookie = 'cookietest=1; SameSite=Strict;';
        const result = d.cookie.indexOf('cookietest=') !== -1;
        // Delete cookie
        d.cookie =
          'cookietest=1; SameSite=Strict; expires=Thu, 01-Jan-1970 00:00:01 GMT';
        return result;
      } catch (_) {
        return false;
      }
    };

    return {
      userAgent,
      screenWidth,
      screenHeight,
      pixelRatio,
      timezone,
      language,
      hasLocalStorage,
      hasSessionStorage,
      canvasFingerprint: getCanvasFingerprint(),
      webGLFingerprint: getWebGLFingerprint(),
      applePay: getApplePay(),
      colorDepth,
      deviceMemory: replaceNaN(toFloat(navigator.deviceMemory), undefined),
      areCookiesEnabled: areCookiesEnabled(),
      hardwareConcurrency: replaceNaN(
        toInt(navigator.hardwareConcurrency),
        undefined,
      ),
      indexedDB: !!window.indexedDB,
      openDatabase: !!window.openDatabase,
    };
  } catch (error) {
    logError('Device fingerprint error:', error);
  }
};

export const getDeviceFingerprint = () => {
  const storedFingerprint = localStorage.getItem('DEVICE_FINGERPRINT');

  if (storedFingerprint) {
    return storedFingerprint;
  }

  const deviceMetadata = getDeviceMetadata();
  const fingerprint = btoa(JSON.stringify(deviceMetadata));

  localStorage.setItem('DEVICE_FINGERPRINT', fingerprint);

  return fingerprint;
};

declare global {
  interface Navigator {
    deviceMemory?: number;
  }

  interface Window {
    ApplePaySession?: {
      canMakePayments: () => boolean;
    };
    openDatabase?: unknown;
  }
}
